import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { numberLocale, useLocale, useT } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { CodeWorkspace } from '@/shared/ui/code-workspace'

const UTILITY = 'html-entities'

type Mode = 'encode' | 'decode'

const NAMED_ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '`': '&#96;',
}

function encodeHtmlEntities(input: string, encodeNonAscii: boolean): string {
  return Array.from(input)
    .map((char) => {
      const named = NAMED_ENTITIES[char]
      if (named) return named

      const codePoint = char.codePointAt(0)
      if (encodeNonAscii && codePoint !== undefined && codePoint > 0x7e) {
        return `&#${codePoint};`
      }

      return char
    })
    .join('')
}

function decodeHtmlEntities(input: string): string {
  if (typeof document === 'undefined') {
    return input
  }

  const textarea = document.createElement('textarea')
  textarea.innerHTML = input
  return textarea.value
}

function countEntities(value: string): number {
  return value.match(/&(?:#[0-9]+|#x[0-9a-f]+|[a-z][a-z0-9]+);/gi)?.length ?? 0
}

function lineCount(value: string): number {
  return value ? value.split('\n').length : 0
}

export function HtmlEntitiesPage() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [input, setInput] = useState('')
  const [mode, setMode] = useState<Mode>('encode')
  const [encodeNonAscii, setEncodeNonAscii] = useState(false)
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const handleModeChange = useCallback((nextMode: Mode) => {
    setMode(nextMode)
    setOutput('')
    setError(null)
    setCopied(false)
  }, [])

  const handleProcess = useCallback(() => {
    if (!input) {
      setOutput('')
      setError(null)
      return
    }

    const start = performance.now()
    toolInvoke(UTILITY, {
      mode,
      encode_non_ascii: encodeNonAscii,
      input_length: input.length,
    })

    try {
      const result = mode === 'encode'
        ? encodeHtmlEntities(input, encodeNonAscii)
        : decodeHtmlEntities(input)
      setOutput(result)
      setError(null)
      toolSuccess(UTILITY, Math.round(performance.now() - start), {
        mode,
        encode_non_ascii: encodeNonAscii,
        input_length: input.length,
        output_length: result.length,
        entity_count: countEntities(mode === 'encode' ? result : input),
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : t.htmlEntities.errors.unknown
      setError(message)
      setOutput('')
      toolError(UTILITY, 'html-entities-process-failed', {
        mode,
        encode_non_ascii: encodeNonAscii,
        input_length: input.length,
      })
    }
  }, [encodeNonAscii, input, mode, t.htmlEntities.errors.unknown])

  useEffect(() => {
    const timeout = window.setTimeout(handleProcess, 200)
    return () => window.clearTimeout(timeout)
  }, [handleProcess])

  const handleCopy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }, [output])

  const handleSwap = useCallback(() => {
    const nextMode = mode === 'encode' ? 'decode' : 'encode'
    setInput(output)
    setOutput('')
    setError(null)
    setCopied(false)
    setMode(nextMode)
  }, [mode, output])

  const handleClear = useCallback(() => {
    setInput('')
    setOutput('')
    setError(null)
    setCopied(false)
  }, [])

  const inputStats = useMemo(() => ({
    chars: input.length,
    lines: lineCount(input),
    entities: countEntities(input),
  }), [input])

  const outputStats = useMemo(() => ({
    chars: output.length,
    lines: lineCount(output),
    entities: countEntities(output),
  }), [output])

  return (
    <ToolPageShell toolKey="htmlEntities" width="wide">
      <CodeWorkspace
        error={error}
        controls={(
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium tracking-normal">
                {t.htmlEntities.mode}
              </span>
              <div className="flex rounded-lg border bg-card p-0.5 shadow-sm">
                <button
                  type="button"
                  onClick={() => handleModeChange('encode')}
                  className={cn(
                    'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
                    mode === 'encode'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t.htmlEntities.encode}
                </button>
                <button
                  type="button"
                  onClick={() => handleModeChange('decode')}
                  className={cn(
                    'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
                    mode === 'decode'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t.htmlEntities.decode}
                </button>
              </div>
            </div>

            <label
              className={cn(
                'flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm shadow-sm',
                mode === 'decode' && 'opacity-60',
              )}
            >
              <input
                type="checkbox"
                checked={encodeNonAscii}
                onChange={(event) => {
                  setEncodeNonAscii(event.target.checked)
                  setOutput('')
                  setError(null)
                }}
                disabled={mode === 'decode'}
                className="h-4 w-4 rounded border-muted-foreground/40 accent-primary"
              />
              <span>{t.htmlEntities.encodeNonAscii}</span>
            </label>

          </div>
        )}
        input={{
          title: mode === 'encode' ? t.htmlEntities.sourceText : t.htmlEntities.entitiesText,
          value: input,
          onChange: (value) => {
            setInput(value)
            setOutput('')
            setError(null)
            setCopied(false)
          },
          placeholder: mode === 'encode'
            ? t.htmlEntities.inputEncodePlaceholder
            : t.htmlEntities.inputDecodePlaceholder,
          ariaLabel: mode === 'encode'
            ? t.htmlEntities.encodeLabel
            : t.htmlEntities.decodeLabel,
          clear: { label: t.htmlEntities.clear, onClick: handleClear },
          stats: [
            {
              label: t.htmlEntities.characters,
              value: inputStats.chars.toLocaleString(localeCode),
            },
            {
              label: t.htmlEntities.lines,
              value: inputStats.lines.toLocaleString(localeCode),
            },
            {
              label: t.htmlEntities.entities,
              value: inputStats.entities.toLocaleString(localeCode),
            },
          ],
        }}
        output={{
          title: t.htmlEntities.resultTitle,
          value: error ? '' : output,
          readOnly: true,
          placeholder: t.htmlEntities.outputPlaceholder,
          ariaLabel: t.htmlEntities.resultLabel,
          actions: (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
              >
                {copied ? t.htmlEntities.copied : t.htmlEntities.copy}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSwap}
              >
                {t.htmlEntities.swap}
              </Button>
            </>
          ),
          stats: output && !error
            ? [
              {
                label: t.htmlEntities.characters,
                value: outputStats.chars.toLocaleString(localeCode),
              },
              {
                label: t.htmlEntities.lines,
                value: outputStats.lines.toLocaleString(localeCode),
              },
              {
                label: t.htmlEntities.entities,
                value: outputStats.entities.toLocaleString(localeCode),
              },
            ]
            : undefined,
        }}
      />

      {output && (
        <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">
                {t.htmlEntities.inputEntities}
              </div>
              <div className="mt-1 text-2xl font-bold tabular-nums">
                {inputStats.entities.toLocaleString(localeCode)}
              </div>
              <div className="text-xs text-muted-foreground">
                {t.htmlEntities.detectedEntities}
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">
                {t.htmlEntities.resultEntities}
              </div>
              <div className="mt-1 text-2xl font-bold tabular-nums">
                {outputStats.entities.toLocaleString(localeCode)}
              </div>
              <div className="text-xs text-muted-foreground">
                {t.htmlEntities.detectedEntities}
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">
                {t.htmlEntities.processing}
              </div>
              <div className="mt-1 text-2xl font-bold tabular-nums">
                {mode === 'encode' ? t.htmlEntities.encode : t.htmlEntities.decode}
              </div>
              <div className="text-xs text-muted-foreground">
                {t.htmlEntities.localOnly}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      <section className="mt-10 space-y-5 border-t pt-8">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.htmlEntities.howTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.htmlEntities.how}
          </p>
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.htmlEntities.whenTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.htmlEntities.when}
          </p>
        </div>
      </section>
    </ToolPageShell>
  )
}
